import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Box, Grid } from "@mui/material";
import { t } from "i18next";
import { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import SubscriptionExpireAlert from "../../../components/Alerts/SubscriptionExpireAlert";
import AppLoader from "../../../components/AppLoader";
import GlobalButton from "../../../components/CustomButton";
import Label from "../../../components/Label";
import TextInput from "../../../components/TextInputs";
import { setUserData } from "../../../lib/slices/userSlice";
import { loginCompany } from "../../../services/company/authenticationApiFn";
import { loginSupplier } from "../../../services/supplier/authenticationApiFn";
import { loginWorker } from "../../../services/worker/authenticationApiFn";
import { encryptPassword, handleOpenSnackBar } from "../../../utils";
import { loginValidation } from "../../../utils/Validation";
import { loginCreator } from "../../../services/creator/creatorApiFn";

const LoginForm = () => {
  const openExpireAlert = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();

  // States
  const [isLoading, setIsLoading] = useState();
  const [showPass, setShowPass] = useState(false);
  const [inputValues, setInputValues] = useState({
    email: "",
    password: "",
  });
  const [inputError, setInputError] = useState({
    email: false,
    isEmailValid: false,
    password: false,
  });

  const handleLoginApiCall = async (api, type) => {
    try {
      const validate = loginValidation(inputValues, setInputError);
      if (validate) {
        return;
      }

      const payload = {
        email: inputValues.email,
        password: encryptPassword(inputValues.password),
      };

      setIsLoading(true);
      const response = await api(payload);

      if (response?.data.status === "success") {
        const userData = response?.data?.data.user;
        const token = response?.data?.data.token;
        const isSubscriptionExpired =
          response?.data?.data?.isSubscriptionExpired;
        const isFreeSubscription = response?.data?.data?.isFree || false;
        const userWithToken = { ...userData, token, role: type };

        const whenNotSubscription =
          type !== "education" &&
          userData?.subscriptionId === null &&
          isFreeSubscription === false;
        const whenSubscriptionExpired =
          type !== "education" &&
          isSubscriptionExpired === true &&
          isFreeSubscription === false;

        if (whenNotSubscription) {
          navigate("/subscriptions", {
            state: {
              type,
              user: userData._id,
              subscriptionId: userData.subscriptionId,
            },
          });
        } else if (whenSubscriptionExpired) {
          openExpireAlert.current.openDialog({
            path: "/subscriptions",
            type,
            user: userData._id,
            subscriptionId: userData.subscriptionId,
          });
        } else {
          localStorage.setItem("user-data", JSON.stringify(userWithToken));
          dispatch(setUserData(userWithToken));
          handleOpenSnackBar("success", response?.data?.message);
          if (userWithToken?.role == "company" && userWithToken.userRole) {
            navigate(userWithToken.userRole.pages[0].path);
          } else if (
            type === "worker" &&
            userWithToken?.availability?.length === 0
          ) {
            navigate(`/worker/schedule`);
          } else {
            navigate(`/${type}/dashboard`);
          }
        }
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginAsCreator = async () => {
    try {
      const validate = loginValidation(inputValues, setInputError);
      if (validate) {
        return;
      }

      const payload = {
        email: inputValues.email,
        password: encryptPassword(inputValues.password),
      };

      setIsLoading(true);
      const response = await loginCreator(payload);

      if (response?.data.status === "success") {
        const userData = response?.data?.data.user;
        const token = response?.data?.data.token;
        const userWithToken = { ...userData, token, role: "creator" };

        localStorage.setItem("user-data", JSON.stringify(userWithToken));
        dispatch(setUserData(userWithToken));
        handleOpenSnackBar("success", response?.data?.message);

        navigate(
          ["pending", "rejected"].includes(userData?.status)
            ? `/creator/onboarding`
            : `/creator/dashboard`
        );
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginAsOther = async () => {
    try {
      const validate = loginValidation(inputValues, setInputError);
      if (validate) {
        return;
      }

      let payload = {
        email: inputValues.email,
        password: inputValues.password,
        role: state.type || "worker",
        name: "John Doe",
      };

      localStorage.setItem("user-data", JSON.stringify(payload));

      if (state.type === "worker") {
        navigate("/worker/dashboard");
      } else if (state.type === "supplier") {
        navigate("/supplier/dashboard");
      } else if (state.type === "provider") {
        navigate("/company/dashboard");
      } else if (state.type === "education") {
        navigate("/education/dashboard");
      } else {
        navigate("/worker/dashboard");
      }
      dispatch(setUserData(payload));
    } catch (error) {}
  };

  const handleChangeValues = (event) => {
    const { name, value } = event.target;
    setInputError((prevError) => ({
      ...prevError,
      [name]: false,
      isEmailValid: name === "email" ? false : inputError.isEmailValid,
    }));
    setInputValues((pre) => ({ ...pre, [name]: value }));
  };

  const handleRenewSubscription = (params) => {
    openExpireAlert.current.closeDialog();
    navigate(params.path, {
      state: {
        type: params.type,
        user: params.user,
        subscriptionId: params.subscriptionId,
      },
    });
  };

  return (
    <Box component={"form"} sx={{ mt: 3 }}>
      <AppLoader isVisible={isLoading} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Label labelText={t("emailAddress")} htmlFor={"user-id"} required />
          <TextInput
            inputError={inputError.email || inputError.isEmailValid}
            helperText={
              inputError.email
                ? t("emailRequired")
                : inputError.isEmailValid
                  ? t("invalidEmailFormat")
                  : null
            }
            id={"user-id"}
            required={true}
            placeholder={`${t("enter")} ${t("emailAddress")}`}
            type={"email"}
            name="email"
            value={inputValues.email}
            onChange={handleChangeValues}
          />
        </Grid>
        <Grid item xs={12}>
          <Label labelText={t("password")} htmlFor={"password"} required />
          <TextInput
            inputError={inputError.password}
            helperText={inputError.password ? t("passwordRequired") : null}
            id={"password"}
            required
            placeholder={`${t("enter")} ${t("password")}`}
            type={showPass ? "text" : "password"}
            name="password"
            value={inputValues.password}
            onChange={handleChangeValues}
            InputEndIcon={
              showPass ? (
                <RemoveRedEyeIcon
                  sx={{ cursor: "pointer" }}
                  onClick={() => setShowPass(false)}
                />
              ) : (
                <VisibilityOffIcon
                  sx={{ cursor: "pointer" }}
                  onClick={() => setShowPass(true)}
                />
              )
            }
          />
        </Grid>
      </Grid>
      {/* <Stack alignItems={"flex-end"} mt={1}>
        <Link
          to={"/forgot-password"}
          className="react-router-link"
          style={{
            color: palette.custom.slateGrey,
          }}
        >
          Forgot Password
        </Link>
      </Stack> */}
      <Box mt={3}>
        <GlobalButton
          variant={"contained"}
          btnText={"login"}
          handleClickBtn={() => {
            if (state.type === "supplier") {
              handleLoginApiCall(loginSupplier, state.type);
            } else if (state.type === "company") {
              handleLoginApiCall(loginCompany, state.type);
            } else if (state.type === "worker") {
              handleLoginApiCall(loginWorker, state.type);
            } else if (state.type === "creator") {
              handleLoginAsCreator();
            } else {
              handleLoginAsOther();
            }
          }}
        />
      </Box>
      <SubscriptionExpireAlert
        ref={openExpireAlert}
        handleRenewSubscription={handleRenewSubscription}
      />
    </Box>
  );
};

export default LoginForm;
